import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../app/config';
import { color } from '../../app/theme';
import { NextResourceName, PlatformType } from '../../app/types';
import { validateHttpsAndHttpPath, validateNo8Path } from '../../app/utils';
import icHelpCircle from '../../assets/images/svg/ic-help-circle.svg';
import IcLinkSvg from '../../assets/images/svg/ic-link.svg';
import IcTagSvg from '../../assets/images/svg/ic-tag.svg';
import { setDefaultTemplate, setState } from '../../features/mainSlice';
import {
  checkResourceLimit,
  saveDefaultTemplate,
} from '../../features/organizationSlice';
import Button from './Button';
import DatePicker from './DatePicker';
import DialogText from './DialogText';
import Dropdown from './Dropdown';
import TextAreaCard from './TextAreaCard';
import TextFieldCardCounter from './TextFieldCardCounter';
import TimePicker from './TimePicker';
import Tooltip from './Tooltip';

const useStyles = makeStyles(
  theme => ({
    root: {
      margin: 'auto',
      width: 560,
      backgroundColor: color.$7,
      borderRadius: 12,
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    },
    contentContainer: {
      overflow: 'auto',
      padding: '24px',
    },
    confirmContainer: {
      display: 'flex',
      alignItems: 'stretch',
    },
    titleContainer: {
      padding: '16px 12px',
      borderBottom: `1px solid ${color.$9}`,
    },
    actionContainer: {
      width: '100%',
      padding: '16px 12px',
    },
    divide: {
      width: 1,
      backgroundColor: color.$9,
    },
    tagContainer: {
      display: 'flex',
      alignItems: 'center',
      margin: '8px 0 0',
      '& > img': {
        display: 'block',
        width: 24,
        Height: 24,
        margin: '0 6px 0 0',
      },
    },
    dropdown: {
      width: '100%',
      margin: '0 0 8px 0',
    },
    textField: {
      display: 'block',
      flex: '1 1 auto',
      width: '100%',
      height: 44,
      fontSize: 15,
      lineHeight: '21px',
      outline: 'none',
      padding: '0 16px',
      borderRadius: 22,
      border: `1px solid ${color.$10}`,
      backgroundColor: color.$7,
      '&:focus': {
        borderColor: color.$1,
      },
    },
    'textField--error': {
      border: `1px solid ${color.$17} !important`,
    },
    externalActionContainer: {
      padding: '0 24px 24px',
    },
    selectTemplateBlock: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '0 0 16px',
    },
    selectTemplate: {
      ...theme.link.primaryGreen.default,
      margin: '0 0 0 4px',
    },
    actionButtonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
    },
    saveTemplateBlock: {
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: 44,
        marginTop: 12,
      },
    },
    date: {
      width: 180,
    },
    time: {
      width: 100,
      marginLeft: 8,
    },
    saveTemplateFormControlLabel: {
      flex: '0 0 auto',
    },
    saveTemplateLabel: {
      fontSize: 15,
      lineHeight: 1,
      color: color.$13,
    },
    saveTemplateName: {
      width: 265,
    },
    opertationBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    templateTitle: {
      marginRight: 12,
      fontSize: 15,
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 6,
      },
    },
    expiredAt: {
      marginRight: 0,
    },
  }),
  {
    name: 'ConfirmCardDialog',
  },
);

function ConfirmCardDialog({
  templateExpiredAt,
  templateId,
  name,
  data,
  onCancel,
  onSubmit,
  messageIndex,
  disableTemplate,
  isSendLoading,
  isMessageCenter,
  isShowSubmit,
  platformType,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { objectId: orgId } = useSelector(store => store.organization);
  const isSaveAsDefaultTemplate = useSelector(
    store => store.ma.isSaveAsDefaultTemplate,
  );
  const [isModified, setIsModified] = useState(false);
  const [isShowMsgTemplate, setIsShowMsgTemplate] = useState(false);
  const [localTitle, setLocalTitle] = useState(data.title);
  const [localType1, setLocalType1] = useState(data.buttons[0].type);
  const [localType2, setLocalType2] = useState(data.buttons[1].type);
  const [localButtonTitle1, setlocalButtonTitle1] = useState(
    data.buttons[0].title,
  );
  const [localButtonTitle2, setlocalButtonTitle2] = useState(
    data.buttons[1].title,
  );
  const [localData1, setLocalData1] = useState(data.buttons[0].data);
  const [localData2, setLocalData2] = useState(data.buttons[1].data);
  const [localTags1, setLocalTags1] = useState(data.buttons[0].tags || []);
  const [localTags2, setLocalTags2] = useState(data.buttons[1].tags || []);
  const tag1 = localTags1.length > 0 ? localTags1[0] : '';
  const tag2 = localTags2.length > 0 ? localTags2[0] : '';
  const [templateName, setTemplateName] = useState(
    name ||
      `${t('types.message-template-type.confirm')} ${moment().format(
        'YYYY-MM-DD HH:mm',
      )}`,
  );
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [expiredAt, setExpiredAt] = useState(templateExpiredAt);

  const buttonTypes = [
    { text: t('templates.button-type.postback'), value: 'postback' },
    { text: t('templates.button-type.url'), value: 'url' },
    // { text: t('templates.button-type.location'), value: 'location' },
  ];

  useEffect(() => {
    return () => {
      dispatch(
        setState({
          isSaveAsDefaultTemplate: false,
        }),
      );
    };
  }, []);

  function validateCardData(cardData) {
    if (
      cardData.title === '' ||
      cardData.title.length >
        (platformType === PlatformType.WHATSAPP ? 1024 : 240)
    )
      return false;
    for (let i = 0; i < cardData.buttons.length; i += 1) {
      if (
        cardData.buttons[i].title === '' ||
        cardData.buttons[i].title.length > 20
      )
        return false;
      if (cardData.buttons[i].type === 'postback') {
        if (
          cardData.buttons[i].data === '' ||
          cardData.buttons[i].data.length > 20
        )
          return false;
      }
      if (cardData.buttons[i].type === 'url') {
        if (
          cardData.buttons[i].data === '' ||
          (!validateHttpsAndHttpPath(cardData.buttons[i].data) &&
            !validateNo8Path(cardData.buttons[i].data))
        )
          return false;
      }
    }
    return true;
  }

  useEffect(() => {
    const getResource = async () => {
      const isShow = await dispatch(
        checkResourceLimit(NextResourceName.MSG_TEMPLATE_LIMIT),
      );
      setIsShowMsgTemplate(isShow);
    };
    getResource();
  }, []);

  const isValid = validateCardData({
    title: localTitle,
    buttons: [
      {
        type: localType1,
        title: localButtonTitle1,
        data: localData1,
        tags: localTags1,
      },
      {
        type: localType2,
        title: localButtonTitle2,
        data: localData2,
        tags: localTags2,
      },
    ],
  });

  function handleSubmit() {
    onSubmit(
      {
        title: localTitle,
        buttons: [
          {
            type: localType1,
            title: localButtonTitle1,
            data: localData1,
            tags: localTags1,
          },
          {
            type: localType2,
            title: localButtonTitle2,
            data: localData2,
            tags: localTags2,
          },
        ],
      },
      templateName,
    );
  }

  async function handleSave() {
    const cardData = {
      title: localTitle,
      buttons: [
        {
          type: localType1,
          title: localButtonTitle1,
          data: localData1,
          tags: localTags1,
        },
        {
          type: localType2,
          title: localButtonTitle2,
          data: localData2,
          tags: localTags2,
        },
      ],
    };
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'confirm',
        templateName,
        [cardData],
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    onCancel();
    dispatch(
      setDefaultTemplate({
        isShow: true,
        templateType: 'confirm',
        messageIndex,
      }),
    );
  }

  async function handleSaveAndUse() {
    const cardData = {
      title: localTitle,
      buttons: [
        {
          type: localType1,
          title: localButtonTitle1,
          data: localData1,
          tags: localTags1,
        },
        {
          type: localType2,
          title: localButtonTitle2,
          data: localData2,
          tags: localTags2,
        },
      ],
    };
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'confirm',
        templateName,
        [cardData],
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    handleSubmit();
    dispatch(
      setDefaultTemplate({
        isShow: false,
        templateType: 'confirm',
        messageIndex,
      }),
    );
  }

  const contentComponent = (
    <div>
      {!disableTemplate && !isMessageCenter && (
        <div className={classes.selectTemplateBlock}>
          {`${t('or')} `}
          <span
            className={classes.selectTemplate}
            onClick={() => {
              onCancel();
              dispatch(
                setDefaultTemplate({
                  isShow: true,
                  templateType: 'confirm',
                  messageIndex,
                }),
              );
            }}
          >
            {t('select-by-default-template')}
          </span>
        </div>
      )}
      <div className={classes.root}>
        <div className={classes.titleContainer}>
          <TextAreaCard
            maxCount={platformType === PlatformType.WHATSAPP ? 1024 : 40}
            placeholder={t('templates.description')}
            value={localTitle}
            onChange={v => {
              setLocalTitle(v);
              setIsModified(true);
            }}
            status={isModified && localTitle === '' ? 'error' : undefined}
          />
        </div>
        <div className={classes.confirmContainer}>
          <div className={classes.actionContainer}>
            <Dropdown
              className={classes.dropdown}
              menuItems={buttonTypes}
              value={localType1}
              onChange={v => {
                setLocalType1(v);
                setlocalButtonTitle1('');
                setLocalData1('');
                setLocalTags1([]);
                setIsModified(true);
              }}
            />
            <TextFieldCardCounter
              maxCount={20}
              placeholder={t('templates.button-placeholder')}
              value={localButtonTitle1}
              onChange={v => {
                if (localType1 !== 'url' && localType1 !== 'location') {
                  setLocalData1(v);
                }
                setlocalButtonTitle1(v);
                setIsModified(true);
              }}
              status={
                isModified && localButtonTitle1 === '' ? 'error' : undefined
              }
            />
            {localType1 === 'url' && (
              <div className={classes.tagContainer}>
                <img src={IcLinkSvg} alt="Link" />
                <input
                  className={classnames(classes.textField, {
                    [classes['textField--error']]:
                      isModified &&
                      (localData1 === '' ||
                        (!validateHttpsAndHttpPath(localData1) &&
                          !validateNo8Path(localData1))),
                  })}
                  value={localData1}
                  placeholder={t('templates.url-placeholder')}
                  onChange={e => {
                    setLocalData1(e.target.value);
                    setIsModified(true);
                  }}
                />
              </div>
            )}
            {localType1 !== 'location' && (
              <div className={classes.tagContainer}>
                <img src={IcTagSvg} alt="Tag" />
                <input
                  className={classes.textField}
                  value={tag1}
                  placeholder={t('templates.tags-platform')}
                  onChange={e => {
                    setLocalTags1([e.target.value]);
                  }}
                />
              </div>
            )}
          </div>
          <div className={classes.divide} />
          <div className={classes.actionContainer}>
            <Dropdown
              className={classes.dropdown}
              menuItems={buttonTypes}
              value={localType2}
              onChange={v => {
                setLocalType2(v);
                setlocalButtonTitle2('');
                setLocalData2('');
                setLocalTags2([]);
                setIsModified(true);
              }}
            />
            <TextFieldCardCounter
              maxCount={20}
              placeholder={t('templates.button-placeholder')}
              value={localButtonTitle2}
              onChange={v => {
                if (localType2 !== 'url' && localType2 !== 'location') {
                  setLocalData2(v);
                }
                setlocalButtonTitle2(v);
                setIsModified(true);
              }}
              status={
                isModified && localButtonTitle2 === '' ? 'error' : undefined
              }
            />
            {localType2 === 'url' && (
              <div className={classes.tagContainer}>
                <img src={IcLinkSvg} alt="Link" />
                <input
                  className={classnames(classes.textField, {
                    [classes['textField--error']]:
                      isModified &&
                      (localData2 === '' ||
                        (!validateHttpsAndHttpPath(localData2) &&
                          !validateNo8Path(localData2))),
                  })}
                  value={localData2}
                  placeholder={t('templates.url-placeholder')}
                  onChange={e => {
                    setLocalData2(e.target.value);
                    setIsModified(true);
                  }}
                />
              </div>
            )}
            {localType2 !== 'location' && (
              <div className={classes.tagContainer}>
                <img src={IcTagSvg} alt="Tag" />
                <input
                  className={classes.textField}
                  value={tag2}
                  placeholder={t('templates.tags-platform')}
                  onChange={e => {
                    setLocalTags2([e.target.value]);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const actionComponent = (
    <div style={{ width: '100%' }}>
      <div className={classes.actionButtonContainer}>
        <div className={classes.saveTemplateBlock}>
          {!disableTemplate && (
            <>
              <div>
                {isMessageCenter ? (
                  <span className={classes.templateTitle}>
                    {t('templateName')}
                  </span>
                ) : isShowMsgTemplate ? (
                  <FormControlLabel
                    className={classes.saveTemplateFormControlLabel}
                    classes={{
                      label: classes.saveTemplateLabel,
                    }}
                    control={
                      <Checkbox
                        checked={isSaveAsDefaultTemplate}
                        onChange={() => {
                          dispatch(
                            setState({
                              isSaveAsDefaultTemplate: !isSaveAsDefaultTemplate,
                            }),
                          );
                        }}
                        name="checkedB"
                        color="primary"
                      />
                    }
                    label={t('save-as-default-template')}
                  />
                ) : null}
                {(isSaveAsDefaultTemplate || isMessageCenter) && (
                  <TextFieldCardCounter
                    className={classes.saveTemplateName}
                    maxCount={0}
                    value={templateName}
                    onChange={v => {
                      setTemplateName(v);
                    }}
                    status={!templateName ? 'error' : undefined}
                    placeholder={t('key-in-template-name')}
                  />
                )}
              </div>
              {(isSaveAsDefaultTemplate || isMessageCenter) && (
                <div>
                  <FormControlLabel
                    className={classnames(
                      classes.saveTemplateFormControlLabel,
                      classes.expiredAt,
                    )}
                    classes={{
                      label: classes.saveTemplateLabel,
                    }}
                    control={
                      <Checkbox
                        checked={!!expiredAt}
                        onChange={e => {
                          if (e.target.checked) {
                            setExpiredAt(
                              moment()
                                .add(
                                  config.DEFAULT_TEMPLATE_EXPIRED_DAY,
                                  'days',
                                )
                                .hour(0)
                                .minute(0),
                            );
                          } else {
                            setExpiredAt(undefined);
                          }
                        }}
                        name="checkedB"
                        color="primary"
                      />
                    }
                    label={
                      <span className={classes.templateTitle}>
                        {t('save-template-expired')}
                        <Tooltip
                          light
                          placement="top"
                          title={t('save-template-expired-tooltip')}
                        >
                          <img
                            className={classes.marginLeft8}
                            src={icHelpCircle}
                            alt="alert"
                          />
                        </Tooltip>
                      </span>
                    }
                  />
                  {!!expiredAt && (
                    <>
                      <DatePicker
                        className={classes.date}
                        selectedDate={expiredAt}
                        minDate={moment()}
                        modalStyle={{
                          top: -430,
                        }}
                        onChange={date => {
                          setExpiredAt(
                            moment(
                              `${moment(date).format('YYYY-MM-DD')} ${moment(
                                expiredAt,
                              ).format('HH:mm')}`,
                            ),
                          );
                        }}
                        placeholder={t('datepicker.selectDate')}
                      />
                      <TimePicker
                        className={classes.time}
                        selectedTime={expiredAt}
                        isUp
                        onChange={value => {
                          setExpiredAt(
                            moment(
                              `${moment(expiredAt).format(
                                'YYYY-MM-DD',
                              )} ${moment(value).format('HH:mm')}`,
                            ),
                          );
                        }}
                        placeholder={t('datepicker.selectDate')}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className={classes.opertationBlock}>
          <Button topic="primaryBlue" onClick={onCancel}>
            {t('modal.cancel')}
          </Button>
          {isSaveAsDefaultTemplate || isMessageCenter ? (
            <>
              <Button
                style={{ margin: '0 0 0 8px' }}
                topic="primaryBlue"
                onClick={handleSave}
                isDisable={!isValid || !templateName || templateName === ''}
                isLoading={isSaveLoading}
              >
                {t('modal.save')}
              </Button>
              {isShowSubmit && (
                <Button
                  style={{ margin: '0 0 0 8px' }}
                  topic="primaryBlue"
                  onClick={handleSaveAndUse}
                  isDisable={!isValid || !templateName || templateName === ''}
                  isLoading={isSaveLoading || isSendLoading}
                >
                  {isMessageCenter
                    ? t('modal.save-and-send')
                    : t('modal.save-and-submit')}
                </Button>
              )}
            </>
          ) : (
            <Button
              style={{ margin: '0 0 0 8px' }}
              topic="primaryBlue"
              onClick={handleSubmit}
              isDisable={!isValid}
            >
              {t('modal.sure')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DialogText
      isOpen
      title={
        isMessageCenter
          ? t('template.create-tooltip')
          : t('templates.confirm-card')
      }
      content={contentComponent}
      contentClass={classes.contentContainer}
      actions={actionComponent}
      actionsClass={classes.externalActionContainer}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      cancelText={t('modal.cancel')}
      submitText={t('modal.sure-2')}
      isSubmitDisabled={!isValid}
    />
  );
}

ConfirmCardDialog.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
  isMessageCenter: false,
  isSendLoading: false,
  isShowSubmit: true,
  data: {
    title: '',
    buttons: [
      {
        type: 'postback',
        title: '',
        data: '',
        tags: [],
      },
      {
        type: 'postback',
        title: '',
        data: '',
        tags: [],
      },
    ],
  },
  platformType: '',
};

ConfirmCardDialog.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        data: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }),
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isMessageCenter: PropTypes.bool,
  isSendLoading: PropTypes.bool,
  isShowSubmit: PropTypes.bool,
  platformType: PropTypes.string,
};

export default ConfirmCardDialog;
